import {
  Button,
  Checkbox,
  FilterIcon,
  InputSearch,
  Pagination,
  Select,
  TableColumnsColumnProps,
  Tag,
  Text,
  Tooltip,
} from '@mercai/clever'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  OrderCenterExpandedColumn,
  OrderDrawerFilter,
  OrderTableColumnCreditOrderStatus,
  OrderTableColumnFinance,
  TableColumnActionsButtons,
  TableColumnBuyer,
  TableColumnDeliveredAt,
  TableColumnExpectedDeliveryDate,
  TableColumnPaymentExpireAt,
  TableColumnsInvoiceStatus,
  TableColumnsLogisticStatus,
  TableColumnsPaymentStatus,
  TableColumnsSalesOrderStatus,
  TableColumnTotalPrice,
  TableCreditColumnDaysToExpire,
  TableCreditColumnExpectedPaymentDate,
  TableReference,
  TableTagMoscow,
  TableTaxStatus,
} from './components'

import { AppliedFilters, SortBy, TableConfigurator } from '@/components'

import { DEFAULT_LIMIT_PAGE } from '@/constants'

import {
  distanceToNow,
  formatCurrency,
  formatDate,
  formatNumber,
  getOrderItemDescription,
  trimString,
} from '@/helpers'

import { SaleOrderItemProps, SaleOrderProps } from '@/types'

import { columnsForType } from './table.constants'

import { OrderCenterTableProps } from './table.type'

import { QUERIES_ADMIN } from '@/services'
import styles from './table.module.css'

export const OrderCenterTable = ({
  type,
  data,
  isLoading,
  filter,
  onSetFilter,
  onEditRegister,
  onShowRegister,
  onDeleteRegister,
  formattedFilter,
  setCheckedRows,
  checkedRows = [],
  headerComponents,
  excludeFilterKeys = [],
}: OrderCenterTableProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenOrderCenterPage')

  const [isOpenDrawerFilter, setIsOpenDrawerFilter] = useState(false)

  const [totalPages, setTotalPages] = useState(1)

  const getContentTotalFiltered = () => {
    const activeFilters = Object.keys(filter)?.filter(
      (key) =>
        !!filter[key] &&
        key !== 'page' &&
        key !== 'limit' &&
        key !== 'tab' &&
        key !== 'ordering',
    ).length

    if (activeFilters > 0) {
      return (
        <Tag variant="primary" model="light" size="small">
          {activeFilters}
        </Tag>
      )
    }

    return ''
  }

  const handleFilter = (changedKey: string, newValue: string | number) => {
    if (changedKey === 'page') {
      onSetFilter({ ...filter, [changedKey]: `${newValue}` })
    } else {
      onSetFilter({ ...filter, [changedKey]: `${newValue}`, page: '1' })
    }
  }

  useEffect(() => {
    if (data?.count) {
      setTotalPages(
        Math.ceil(data?.count / +(filter?.limit || DEFAULT_LIMIT_PAGE)),
      )
    }
  }, [data])

  const getContentProduct = useCallback(
    (saleOrdersItems?: SaleOrderProps['items'], index = 0) => {
      const concatItems = saleOrdersItems
        ?.map((item) => getOrderItemDescription(item as SaleOrderItemProps))
        .join(', ')

      return (
        <Tooltip
          text={concatItems || ''}
          widthText="18.75rem"
          orientation={index >= 8 ? 'top' : 'bottom'}
        >
          {trimString(concatItems || '', 52, '...')}
        </Tooltip>
      )
    },
    [data],
  )

  const getQuantityProducts = useCallback(
    (saleOrdersItems: SaleOrderProps['items'] = []) => {
      return saleOrdersItems?.reduce((acc, current) => {
        return (acc += current?.quantity)
      }, 0)
    },
    [data],
  )

  const columns: TableColumnsColumnProps<SaleOrderProps>[] = [
    ...(setCheckedRows
      ? [
        {
          key: 'checkbox',
          width: '2.5rem',
          render: ({ record }: { record: SaleOrderProps }) => (
            <Checkbox
              value={checkedRows?.includes(record.uuid)}
              onChange={() => {
                if (checkedRows?.includes(record.uuid)) {
                  setCheckedRows(
                    checkedRows.filter((uuid) => uuid !== record.uuid),
                  )
                } else {
                  setCheckedRows([...checkedRows, record.uuid])
                }
              }}
            />
          ),
        },
      ]
      : []),
    {
      title: t('tableReference'),
      key: 'reference',
      dataKey: 'reference',
      width: '9rem',
      render: ({ value, record }) => (
        <TableReference
          reference={value || ''}
          saleOrderStatus={record.status}
        />
      ),
    },
    {
      title: t('tableBuyer'),
      key: 'buyer',
      width: '17rem',
      sortKey: 'buyer_profile__company_name',
      dataKey: 'buyer',
      render: ({ record }) => (
        <TableColumnBuyer
          buyerName={
            record?.buyer?.usersNames
              ? record?.buyer?.usersNames?.join(', ')
              : ''
          }
          companyName={record?.buyer?.companyName}
          uuid={record?.buyer?.uuid}
        />
      ),
    },
    {
      title: t('tableTaxStatus'),
      key: 'taxStatus',
      sortKey: 'buyer_profile__tax_status',
      width: '8rem',
      render: ({ record }) => (
        <TableTaxStatus taxStatus={record?.buyer?.taxStatus} />
      ),
    },
    {
      title: t('tableTime'),
      key: 'createdAtDistanceToNow',
      dataKey: 'buyer.createdAt',
      width: '16rem',
      render: ({ value }) => (value ? distanceToNow(value) : '-'),
    },
    {
      title: t('tableProducts'),
      key: 'items',
      width: '20rem',
      render: ({ record, index }) => getContentProduct(record?.items, index),
    },
    {
      title: t('tableAmount'),
      key: 'totalPrice',
      sortKey: 'total_order_price_ordering',
      dataKey: 'items',
      render: ({ record }) => <TableColumnTotalPrice saleOrder={record} />,
    },
    {
      title: t('tableQuantity'),
      key: 'totalQuantity',
      dataKey: 'items',
      render: ({ record }) =>
        formatNumber(getQuantityProducts(record.items) || 0, 'es-MX'),
    },
    {
      title: t('tableMoscow'),
      key: 'moscowClassification',
      dataKey: 'buyer.moscowClassification',
      render: ({ value }) => <TableTagMoscow moscowClassification={value} />,
    },
    {
      title: t('tableSellerName'),
      key: 'keyAccountManager',
      dataKey: 'keyAccountManager.email',
      width: '18.75rem',
    },
    {
      title: t('tableStatePayment'),
      key: 'paymentStatus',
      sortKey: 'payments__status',
      width: '9rem',
      render: ({ record }) => (
        <TableColumnsPaymentStatus
          calculatedPaymentStatus={record?.paymentsStatus || 'processing'}
        />
      ),
    },
    {
      title: t('tableStateInvoice'),
      key: 'invoiceStatus',
      sortKey: 'finance_order',
      width: '12rem',
      render: ({ record }) => (
        <TableColumnsInvoiceStatus
          invoiceStatus={
            record?.invoices?.[0]?.status || record?.invoicesStatus
          }
        />
      ),
    },
    {
      title: t('tablePaidAt'),
      key: 'paidAt',
      render: ({ record }) =>
        record?.payments?.[0]?.paidAt
          ? formatDate(record?.payments?.[0]?.paidAt)
          : '-',
    },
    {
      title: t('tableCogs'),
      key: 'cogs',
      render: ({ record }) =>
        record?.totalOrderCost
          ? formatCurrency(record?.totalOrderCost, 'es-MX', 'USD')
          : '-',
    },
    {
      title: t('tableLogisticStatus'),
      key: 'logisticStatus',
      sortKey: 'shipments__status',
      width: '13rem',
      render: ({ record }) =>
        record?.shipments?.map((shipment) => (
          <TableColumnsLogisticStatus
            shipment={shipment}
            saleOrderUuid={record?.uuid}
            queryKeys={[[QUERIES_ADMIN.ORDER_CENTER.module]]}
          />
        )),
    },
    {
      title: t('tableSalesOrderStatus'),
      key: 'salesOrderStatus',
      width: '8rem',
      render: ({ record }) => (
        <TableColumnsSalesOrderStatus salesOrderStatus={record?.status} />
      ),
    },
    {
      title: t('tableExpectedDeliveryDate'),
      key: 'expectedDeliveryAt',
      sortKey: 'shipments__expected_delivery_at',
      width: '11.5rem',
      render: ({ record }) => (
        <TableColumnExpectedDeliveryDate
          expectedDeliveryAt={record?.shipments?.[0]?.expectedDeliveryAt}
          deliveryDate={record?.shipments?.[0]?.deliveredAt}
        />
      ),
    },
    {
      title: t('tableDaysToExpire'),
      key: 'daysToExpire',
      width: '14rem',
      render: ({ record }) => (
        <TableCreditColumnDaysToExpire
          expiresAt={record?.creditOrders?.[0]?.expectedPaymentDate}
        />
      ),
    },
    {
      title: t('tablePaymentExpiresAt'),
      key: 'paymentExpiresAt',
      width: '16rem',
      render: ({ record }) => (
        <TableColumnPaymentExpireAt
          expiresAt={record?.payments?.[0]?.expiresAt}
        />
      ),
    },
    {
      title: t('tableDeliveredAt'),
      key: 'deliveredAt',
      width: '14rem',
      sortKey: 'shipments__delivered_at',
      render: ({ record }) => (
        <TableColumnDeliveredAt
          deliveredAt={record?.shipments?.[0]?.deliveredAt}
        />
      ),
    },
    {
      title: t('tableExpectedPaymentDate'),
      key: 'expectedPaymentDate',
      sortKey: 'credit_orders__expected_payment_date',
      width: '15rem',
      render: ({ record }) => (
        <TableCreditColumnExpectedPaymentDate
          expectedPaymentDate={record?.creditOrders?.[0]?.expectedPaymentDate}
        />
      ),
    },
    {
      title: t('tableTransportPrice'),
      key: 'transportPrice',
      render: ({ record }) =>
        formatCurrency(record?.totalFreightPrice || 0, 'es-MX', 'USD'),
    },
    {
      title: t('tableDeliveryOption'),
      key: 'deliveryOption',
      width: '11.6rem',
      render: ({ record }) => (
        <Text>
          {record?.shipments?.[0]?.deliveryOption
            ? tEnums(
              `logistic_shipment_delivery_option_${record.shipments[0].deliveryOption}`,
            )
            : '-'}
        </Text>
      ),
    },
    {
      title: t('tableOrderCreditStatus'),
      key: 'creditOrderStatus',
      width: '10rem',
      render: ({ record }) =>
        record?.creditOrders?.[0]?.status ? (
          <OrderTableColumnCreditOrderStatus
            value={record.creditOrders[0].status}
          />
        ) : (
          '-'
        ),
    },
    {
      title: t('tableOrderFinance'),
      key: 'orderFinance',
      width: '14.5rem',
      render: ({ record }) => (
        <OrderTableColumnFinance
          cm3Margin={record?.cm3Margin}
          grossMargin={record?.grossMargin}
        />
      ),
    },
  ]

  const actionsColumn: TableColumnsColumnProps<SaleOrderProps> = {
    key: 'tableActionsButtons',
    dataKey: 'uuid',
    width: '6rem',
    render: ({ record }) => (
      <TableColumnActionsButtons
        onClickEdit={() => onEditRegister(record.uuid)}
        onClickView={() => onShowRegister(record.uuid)}
        onClickDelete={() => onDeleteRegister(record.uuid)}
      />
    ),
  }

  const onHandleSortBy = (ordering: string) => {
    if (ordering === filter.ordering) {
      return handleFilter('ordering', '')
    }

    handleFilter('ordering', ordering)
  }

  const sorts = [
    {
      title: t('orderingCreatedAtButton'),
      sortBy: 'created_at',
    },
    ...(['finance'].includes(type)
      ? [
        {
          title: t('orderingExpectedDeliveryDate'),
          sortBy: 'shipments__expected_delivery_date',
        },
        {
          title: t('orderingExpectedDeliveryAt'),
          sortBy: 'shipments__expected_delivery_at',
        },
        {
          title: t('orderingDeliveryAt'),
          sortBy: 'shipments__delivery_at',
        },
        {
          title: t('orderingPaymentsPaidAt'),
          sortBy: 'payments__paid_at',
        },
      ]
      : []),
  ]

  const Header = () => (
    <div>
      <div className={styles.filter}>
        <div className={styles['filter-group']}>
          <InputSearch
            value={filter.search as string}
            onChange={(value) => handleFilter('search', value)}
            className={styles['search-input']}
            translations={{
              inputPlaceholder: t('tableSearchPlaceholder'),
            }}
          />

          <Button
            variant="secondary"
            onClick={() => setIsOpenDrawerFilter(true)}
          >
            <FilterIcon />
            {t('tableFilterButton')}
            {getContentTotalFiltered()}
          </Button>

          <SortBy
            sortBy={filter?.ordering}
            sorts={sorts}
            onHandleSortBy={onHandleSortBy}
          />

          {headerComponents}
        </div>
      </div>

      <AppliedFilters
        filtersApplied={filter}
        excludeKeys={[
          'page',
          'limit',
          'ordering',
          'shipmentExpectedDeliveryAtAfter',
          'shipmentExpectedDeliveryAtBefore',
          'tab',
          ...excludeFilterKeys,
        ]}
        onRemoveFilter={(key) => handleFilter(key, '')}
        translations={{
          filterAppliedText: t('filterApplied'),
        }}
        keyTranslations={{
          order_status: t('filterOrderStatus'),
          payment_status: t('filterPaymentStatus'),
          invoice_status: t('filterInvoiceStatus'),
          shipment_status: t('filterShipmentStatus'),
          tax_status: t('drawerFilterTaxStatusTitle'),
          moscow: t('filterMoscowClassification'),
          root_category: t('filterRootCategory'),
          key_account_manager: t('filterKeyAccountManager'),
          created_at_before: t('filterCreatedAtBefore'),
          created_at_after: t('filterCreatedAtAfter'),
          credit_expected_payment_date_before: t(
            'filterExpectedPaymentDateBefore',
          ),
          credit_expected_payment_date_after: t(
            'filterExpectedPaymentDateAfter',
          ),
          is_sample: t('filterIsSample'),
          classification_group: t('filterClassificationGroup'),
          search: t('filterSearch'),
          credit_status: t('filterCreditStatus'),
        }}
        renderValue={(key, value) => {
          if (key === 'created_at_before' || key === 'created_at_after') {
            return formatDate(value)
          }

          if (key === 'key_account_manager') {
            return formattedFilter?.key_account_manager || ''
          }

          if (key === 'is_sample') {
            return value === 'true'
              ? t('filterIsSampleYesLabel')
              : t('filterIsSampleNoLabel')
          }

          const objectsTranslations = {
            order_status: tEnums(`sale_order_status_${value}`),
            payment_status: tEnums(`order_payment_status_${value}`),
            invoice_status: tEnums(`order_invoice_status_${value}`),
            shipment_status: tEnums(`logistic_shipment_status_${value}`),
            tax_status: tEnums(`profile_tax_status_${value}`),
            moscow: tEnums(`profile_moscow_${value}`),
            root_category: tEnums(`root_category_${value}`),
            classification_group: tEnums(`classification_group_${value}`),
            credit_status: tEnums(`credit_order_status_${value}`),
          }

          return objectsTranslations[key as keyof typeof objectsTranslations]
        }}
      />
    </div>
  )

  return (
    <div className={styles.core}>
      <TableConfigurator<SaleOrderProps>
        key={`@mercai/order-center-${type}-columns-v4`}
        cookieKey={`@mercai/order-center-${type}-columns-v4`}
        classNames={{
          bodyColumn: styles['body-column'],
        }}
        isLoading={isLoading}
        columns={columns}
        actionsColumn={actionsColumn}
        data={data?.results || []}
        expandableComponent={({ record }) => (
          <OrderCenterExpandedColumn record={record} type={type} />
        )}
        sortBy={filter.ordering}
        onSortBy={onHandleSortBy}
        header={<Header />}
        initialHidden={
          columns
            .filter((column) => !columnsForType[type].includes(column.key))
            ?.map((col) => col.key) || []
        }
      />

      <div className={styles.pagination}>
        <div className={styles['pagination-show-registers']}>
          <Text>
            {t('tableShowRegisters', {
              showNumber:
                (data?.count || 0) < +filter.limit
                  ? data?.count
                  : +filter.limit,
              totalNumber: data?.count || 0,
            })}
          </Text>

          <Select
            value={filter.limit}
            onChange={(value) => handleFilter('limit', value)}
            options={[
              { label: '10', value: '10' },
              { label: '20', value: '20' },
              { label: '50', value: '50' },
              { label: '100', value: '100' },
            ]}
          />
        </div>

        <Pagination
          totalPages={totalPages}
          onChange={(page) => handleFilter('page', page)}
          currentPage={+filter.page as number}
        />
      </div>

      <OrderDrawerFilter
        isOpen={isOpenDrawerFilter}
        onHandleOpen={() => setIsOpenDrawerFilter((prev) => !prev)}
        filter={filter}
        onHandleFilter={onSetFilter}
        type={type}
      />
    </div>
  )
}
